import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fpPath } from "@/lib/site";
import { X, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * ToolkitOnboardingTour — one-time 3-step welcome shown to a farmer on
 * their first visit to /toolkit (or /farmers/toolkit). Dismiss is sticky
 * via localStorage so returning visitors never see it again.
 *
 * Storage key: fp:toolkit-tour-seen  (value: "1")
 */
const KEY = "fp:toolkit-tour-seen";

const STEPS = [
  {
    eyebrow: "Meet the toolkit",
    title: "Grow it. Manage it. Sell it. Share it.",
    body: "Twenty-four tools built for real farm days — twelve run the farm (profile, catalog, map, voice diary, weather actions, planning) and twelve are a Marketing Suite that finds and keeps customers.",
  },
  {
    eyebrow: "Two shelves, one toolkit",
    title: "Farm Management + Marketing Suite.",
    body: "Flip between the two shelves at the top of the toolkit. Each tool has a preview screen so you know what it does before you tap in.",
  },
  {
    eyebrow: "Always one tap away",
    title: "The Toolkit tab is at the top.",
    body: "Some tools are shipping today, some are landing over the next few weeks. The Season toggle keeps the shipping ones on top.",
  },
];

export default function ToolkitOnboardingTour() {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch { /* private mode — skip */ }
  }, []);

  if (!open) return null;

  const close = (persist = true) => {
    try { if (persist) localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <div
      data-testid="toolkit-tour"
      className="fixed inset-0 z-[2000] flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm px-4 pb-6 md:pb-0"
      role="dialog" aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-3xl bg-[#F5EBDC] border border-black/10 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={11} weight="fill" className="inline mr-1" /> {step.eyebrow}
          </div>
          <button
            onClick={() => close(true)}
            data-testid="toolkit-tour-close"
            className="rounded-full p-1 text-[#3A3830] hover:bg-black/5"
            aria-label="Close tour"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="px-5 pb-5">
          <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight text-[#151312]">
            {step.title}
          </h2>
          <p className="mt-2 text-[#3A3830]">{step.body}</p>

          {/* Progress dots */}
          <div className="mt-4 flex items-center gap-1.5" data-testid="toolkit-tour-progress">
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? "w-6 bg-[color:var(--np-brand,#B85F2A)]" : "w-1.5 bg-black/15"
                }`}
              />
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => close(true)}
              data-testid="toolkit-tour-skip"
              className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#6C685E] hover:text-[#151312]"
            >
              Skip
            </button>

            {last ? (
              <button
                onClick={() => { close(true); navigate(fpPath("/toolkit")); }}
                data-testid="toolkit-tour-start"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
              >
                Explore the toolkit <ArrowRight size={11} weight="bold" />
              </button>
            ) : (
              <button
                onClick={() => setI(i + 1)}
                data-testid="toolkit-tour-next"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
              >
                Next <ArrowRight size={11} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
